/**
 * Identité de l'entreprise — source unique.
 *
 * Pourquoi ce fichier existe : l'adresse du siège était recopiée à la main dans
 * vingt-deux endroits — pied de page, barre du haut, formulaires, deux blocs
 * JSON-LD distincts, quatre pages légales, llms.txt, le gabarit de l'éditeur
 * d'administration. Au déménagement du siège de Herstal vers Seraing, le site a
 * servi pendant un temps deux adresses différentes sur la même page : le corps
 * de texte annonçait Seraing, le pied de page et le balisage structuré
 * disaient encore Herstal.
 *
 * Une adresse qui figure sur des mentions légales et dans un `PostalAddress`
 * n'a pas le droit d'exister en vingt-deux exemplaires. Elle est ici, et
 * nulle part ailleurs.
 *
 * ⚠️ Le SIÈGE SOCIAL et le DÉPÔT sont deux lieux distincts. Les camions partent
 * du dépôt, et c'est depuis lui que sont mesurées toutes les distances de
 * `src/data/communes.json`. Confondre les deux avait déjà faussé trente-sept
 * relevés. Ne jamais substituer l'un à l'autre.
 *
 * ⚠️ Ce commentaire nommait le dépôt — « rue de la Digue, à Oupeye ». Le nom a
 * été retiré : personne n'a pu le sourcer, et une note de code écrite par
 * quelqu'un qui croyait savoir a suffi à le faire inscrire dans le registre de
 * faits, puis servir dans llms.txt et dans un outil WebMCP. Voir `lieux` dans
 * src/data/registre.ts. Ne pas le réintroduire sans confirmation de Gramme.
 */

export const ENTREPRISE = {
  raisonSociale: 'Déménagements Gramme',
  formeJuridique: 'Société à responsabilité limitée (SRL)',
  tva: 'BE 0775.264.382',

  /**
   * Siège social, voie du Belvédère 1 à Seraing depuis août 2026.
   *
   * Le code postal et la position ont été confirmés par géocodage
   * indépendant : 4100, 50.60303 / 5.52728, à une quinzaine de mètres des
   * coordonnées communiquées par Gramme. Les fonds de carte tiers portaient
   * encore le nom de l'occupant précédent lors de la vérification — l'adresse
   * et le point, eux, sont exacts.
   */
  adresse: {
    rue: 'Voie du Belvédère 1',
    codePostal: '4100',
    localite: 'Seraing',
    province: 'Liège',
    pays: 'Belgique',
    codePays: 'BE',
  },

  geo: {
    latitude: 50.6031529,
    longitude: 5.5273906,
  },

  /**
   * Horaires du bureau — source unique, et elle n'existait pas.
   *
   * Le site en servait DEUX versions contradictoires, et la contradiction
   * portait précisément sur les surfaces destinées aux machines :
   *
   *   Footer, /contact, JSON-LD   « Lun 8h–18h | Mar–Ven 8h–17h », samedi non
   *                               déclaré — trois surfaces d'accord ;
   *   llms.txt, outil WebMCP      « lundi à vendredi de 8h à 18h, samedi de 8h
   *                               à 12h » — une ouverture le samedi que le
   *                               site visible n'annonce nulle part.
   *
   * C'est exactement le cas contre lequel l'en-tête de ce fichier met en garde,
   * aggravé d'un cran : un assistant qui lit llms.txt ou appelle
   * `get_company_info` répondait « oui, ils ouvrent le samedi matin » à
   * quelqu'un qui trouvera porte close.
   *
   * Les trois surfaces visibles font foi. Le samedi n'est PAS déclaré ouvert —
   * pas plus qu'il n'est déclaré fermé : le site ne l'a jamais dit, et
   * l'inventer dans un sens ou dans l'autre serait refaire la même faute. La
   * FAQ y renvoie donc vers un appel (voir la catégorie `escalade`).
   *
   * ⚠️ À faire confirmer par Gramme. Tant que ce n'est pas fait, ne rien
   * ajouter ici : une case vide est réparable, un horaire faux ne l'est pas.
   */
  horaires: {
    lundi: { ouverture: '08:00', fermeture: '18:00' },
    mardiAuVendredi: { ouverture: '08:00', fermeture: '17:00' },
    /** Affichage retenu par le pied de page, la page contact et le JSON-LD. */
    affichage: 'Lun : 8h–18h | Mar–Ven : 8h–17h',
    /** Phrase complète, pour llms.txt et les outils destinés aux agents. */
    phrase:
      'Bureau ouvert le lundi de 8h à 18h et du mardi au vendredi de 8h à 17h. ' +
      'Fermé le dimanche. Pour le samedi, nous appeler.',
    dimancheFerme: true,
    /** Le samedi n'est pas documenté. Ne pas le déduire. */
    samedi: null,
  },

  telephone: {
    affichage: '04 264 50 16',
    international: '+32(0)4 264 50 16',
    lien: '+3242645016',
  },
  fax: '+32(0)4 264 37 73',
  email: 'contact@demenagements-gramme.be',
} as const;

/** « Voie du Belvédère 1, 4100 Seraing » — pied de page, barre du haut, formulaires. */
export const ADRESSE_COURTE =
  `${ENTREPRISE.adresse.rue}, ${ENTREPRISE.adresse.codePostal} ${ENTREPRISE.adresse.localite}`;

/** « Voie du Belvédère 1, 4100 Seraing (Liège), Belgique » — pages légales. */
export const ADRESSE_LEGALE =
  `${ADRESSE_COURTE} (${ENTREPRISE.adresse.province}), ${ENTREPRISE.adresse.pays}`;

/** « Voie du Belvédère 1, 4100 Seraing, Belgique » — courrier, llms.txt, WebMCP. */
export const ADRESSE_POSTALE =
  `${ADRESSE_COURTE}, ${ENTREPRISE.adresse.pays}`;

/** Nœud `PostalAddress` de schema.org, identique dans tous les blocs JSON-LD. */
export const POSTAL_ADDRESS = {
  '@type': 'PostalAddress',
  streetAddress: ENTREPRISE.adresse.rue,
  addressLocality: ENTREPRISE.adresse.localite,
  postalCode: ENTREPRISE.adresse.codePostal,
  addressCountry: ENTREPRISE.adresse.codePays,
} as const;

/** Nœud `GeoCoordinates` de schema.org. */
export const GEO_COORDINATES = {
  '@type': 'GeoCoordinates',
  latitude: ENTREPRISE.geo.latitude,
  longitude: ENTREPRISE.geo.longitude,
} as const;

/**
 * Carte intégrée, construite à partir des coordonnées plutôt que d'un
 * identifiant de lieu.
 *
 * L'ancienne iframe portait un jeton `pb=` opaque, impossible à relire et
 * pointant l'adresse précédente. Une requête sur les coordonnées affiche le
 * bon point sans dépendre d'une fiche d'établissement tierce, dont le libellé
 * peut être périmé.
 */
export const CARTE_EMBED_URL =
  `https://maps.google.com/maps?q=${ENTREPRISE.geo.latitude},${ENTREPRISE.geo.longitude}&z=16&output=embed`;
